import {ResponsiveTable, useBreakPoints} from '@hconnect/uikit'
import {Delete, KeyboardArrowDown as KeyboardArrowDownIcon} from '@mui/icons-material'
import {Typography, Box, Select, MenuItem, TextField, Button, Paper, Checkbox} from '@mui/material'
import {useMemo} from 'react'

import {useTranslation} from '../../../../../../hooks/useTranslation'
import {useChangeableFieldsSettings} from '../../hooks/orderChangeSettings'
import {OrderChangeConfigOptions} from '../../types/OrderChangeConfig'

import {useStyles} from './ChangeableFieldsSettings.styles'

type Props = {
  configOptions: OrderChangeConfigOptions
}

export const ChangeableFieldsSettings = ({configOptions}: Props) => {
  const {t} = useTranslation()
  const {classes} = useStyles()
  const screenSizes = useBreakPoints()
  const isMobile = ['xs', 'sm'].includes(screenSizes)
  const {fields, availableFields, addField, changeField, deleteField} =
    useChangeableFieldsSettings(configOptions)

  const sortedFieldSettings = useMemo(() => {
    if (!fields || !configOptions) return []
    return [...fields].sort(
      (a, b) =>
        configOptions.availableFields[a.fieldName].sortKey -
        configOptions.availableFields[b.fieldName].sortKey
    )
  }, [configOptions, fields])

  const remainingFields = useMemo(() => {
    if (!fields || !availableFields) return []
    const usedFieldNames = fields.map((setting) => setting.fieldName)
    return availableFields.filter((fieldName) => !usedFieldNames.includes(fieldName))
  }, [fields, availableFields])

  const columns = [
    {
      field: 'fieldName',
      headerName: t('configurations.hconnect.orderManagement.configurationDetails.fieldName')
    },
    {
      field: 'editLeadTime',
      headerName: t('configurations.hconnect.orderManagement.configurationDetails.editLeadTime')
    },
    {
      field: 'leadTimeUom',
      headerName: t('configurations.hconnect.orderManagement.configurationDetails.leadTimeUom')
    },
    {
      field: 'requestsPendingApprovalPermitted',
      headerName: t(
        'configurations.hconnect.orderManagement.configurationDetails.manualApproveFlag'
      )
    },
    {field: 'actions', headerName: ''}
  ]

  return (
    <Box className={classes.container}>
      <Typography className={classes.title}>
        {t(
          'configurations.hconnect.orderManagement.configurationDetails.orderChange.changeableFields.title'
        )}
      </Typography>
      <Box className={classes.addFieldBox}>
        <Select
          value=""
          IconComponent={KeyboardArrowDownIcon}
          onChange={(e) => addField(e.target.value)}
          displayEmpty
          inputProps={{'aria-label': 'Without label'}}
          className={classes.select}
          data-test-id="order-change-field-settings-add-field"
        >
          <MenuItem value="" disabled className={classes.selectMenu}>
            {t(
              'configurations.hconnect.orderManagement.configurationDetails.orderChange.changeableFields.addField'
            )}
          </MenuItem>
          {remainingFields.map((key) => (
            <MenuItem key={key} value={key} className={classes.selectMenu}>
              {configOptions.availableFields[key].fieldName}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Paper>
        <ResponsiveTable
          columns={columns}
          rows={sortedFieldSettings.map((item) => ({
            fieldName: configOptions.availableFields[item.fieldName].fieldName,
            editLeadTime: (
              <TextField
                type="number"
                value={item.editLeadTime}
                onChange={(e) => {
                  const value = e.target.value
                  if (Number(value) >= 0) {
                    changeField(item.fieldName, 'editLeadTime', Number(value))
                  }
                }}
                className={classes.select}
                data-test-id={`order-change-field-settings-edit-lead-time-${item.fieldName}`}
              />
            ),
            leadTimeUom: (
              <Select
                value={item.editLeadTimeUom}
                onChange={(e) => changeField(item.fieldName, 'editLeadTimeUom', e.target.value)}
                IconComponent={KeyboardArrowDownIcon}
                className={classes.select}
                data-test-id={`order-change-field-settings-edit-lead-time-uom-${item.fieldName}`}
              >
                {configOptions.leadTimeUnitOfMeasures.map((uom) => (
                  <MenuItem key={uom} value={uom} className={classes.selectMenu}>
                    {t(`configurations.hconnect.orderManagement.configurationDetails.uom.${uom}`)}
                  </MenuItem>
                ))}
              </Select>
            ),
            requestsPendingApprovalPermitted: (
              <Checkbox
                checked={item.requestsPendingApprovalPermitted}
                onChange={(e) => {
                  return changeField(
                    item.fieldName,
                    'requestsPendingApprovalPermitted',
                    e.target.checked
                  )
                }}
                color="primary"
                data-test-id={`order-change-field-settings-manual-approve-flag-${item.fieldName}`}
              />
            ),
            actions: (
              <Button
                variant="text"
                onClick={() => deleteField(item.fieldName)}
                className={classes.deleteButton}
              >
                <Delete />
              </Button>
            )
          }))}
          keyField="fieldName"
          onSort={() => {}}
          isMobile={isMobile}
          data-test-id="order-change-field-settings-table"
        />
      </Paper>
    </Box>
  )
}
